import { Dispatch, ForwardedRef, forwardRef, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SidePanel } from '@mews/b2b-ui';
import { DropDownButton, DropdownItem, IconName, Stack } from '@optimus-web/core';
import { AssistantSmartTipsEntity } from '@mews-commander/data-access';
import { lt, useGainsightService } from '@mews/framework';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { useResolveRoute } from '@mews/routing';
import { RouteName } from '@mews-commander/enums';
import { SearchType } from '../assistantSearchTypes';
import { navigateInBaseModal } from '../../app/actions/common';
import { SearchStateProps } from '../Assistant';
import { AssistantListItemSelectionStateProps } from '../drawer/hooks/useAssistantListItemSelectionHandler';
import { useSummarySearchTypeHook } from './hooks/useSummarySearchTypeHook';

export interface SummaryDialogProps {
    itemId: string;
    searchType: SearchType;
    entity: AssistantSmartTipsEntity;
    setSearchState: Dispatch<SetStateAction<SearchStateProps>>;
    enterpriseIds: Array<string>;
    setListItemSelectionState: Dispatch<SetStateAction<AssistantListItemSelectionStateProps>>;
    openSummaryPanel: boolean;
}

const EntityMenuButton: React.FC<{
    entity: AssistantSmartTipsEntity;
    itemId: string;
    dispatch: CommanderThunkDispatch;
    closeAndResetSearch: () => void;
}> = ({ entity, itemId, dispatch, closeAndResetSearch }) => {
    const resolveRoute = useResolveRoute();
    const viewProfile = useCallback(() => {
        const url = resolveRoute(RouteName.CustomerDetail, { id: itemId });
        dispatch(navigateInBaseModal(url));
        closeAndResetSearch();
    }, [closeAndResetSearch, dispatch, itemId, resolveRoute]);

    switch (entity) {
        case AssistantSmartTipsEntity.Customer:
            return (
                <DropDownButton variant="tertiary" icon={IconName.More} size="small" data-test-id="More">
                    <DropdownItem label={lt('ViewGuest')} onClick={viewProfile} data-test-id="ViewGuest" />
                </DropDownButton>
            );
        default:
            return null;
    }
};
export const SummarySidePanel = forwardRef(
    (
        {
            itemId,
            enterpriseIds,
            setSearchState,
            entity,
            setListItemSelectionState,
            openSummaryPanel,
        }: SummaryDialogProps,
        ref: ForwardedRef<HTMLDivElement>
    ) => {
        const [loading, setLoading] = useState(false);
        const [opened, setOpened] = useState(false);
        const dispatch: CommanderThunkDispatch = useDispatch();
        const gainSightService = useGainsightService();
        const handleCloseSummarySidePanel = useCallback(() => {
            setListItemSelectionState((prev) => ({
                ...prev,
                openSummary: false,
            }));
        }, [setListItemSelectionState]);

        const summaryType = useSummarySearchTypeHook({
            searchType: SearchType.Customer,
            itemId,
            enterpriseIds,
            handleCloseSummarySidePanel,
            setSearchState,
            setListItemSelectionState,
            summaryPanelOpened: opened,
        });

        useEffect(() => {
            if (openSummaryPanel) {
                setOpened(true);
                gainSightService.trackEvent('AssistantShortcutEnterOpenSummary');
            } else {
                setOpened((prev) => {
                    if (prev) {
                        gainSightService.trackEvent('AssistantShortcutEnterCloseSummary');
                    }
                    return false;
                });
            }
        }, [gainSightService, openSummaryPanel]);

        /** This effect helps with the smooth transition when we open/close the summary side panel */
        useEffect(() => {
            let timeout: number | undefined;
            if (opened) {
                setLoading(true);
                timeout = window.setTimeout(() => setLoading(false), 500);
            }

            return () => {
                window.clearTimeout(timeout);
            };
        }, [opened]);

        return useMemo(() => {
            const { actions } = summaryType;
            const content = <Stack ref={ref}>{loading ? null : summaryType.component}</Stack>;

            const closeAndResetSearch = () => {
                setSearchState((prevState) => ({
                    ...prevState,
                    searchTerm: '',
                    focused: false,
                }));
                handleCloseSummarySidePanel();
            };

            const headerActions = (
                <EntityMenuButton
                    entity={entity}
                    itemId={itemId}
                    dispatch={dispatch}
                    closeAndResetSearch={closeAndResetSearch}
                />
            );

            return (
                <SidePanel
                    loading={loading}
                    width="small"
                    opened={opened}
                    title={summaryType.title ? summaryType.title : lt('Loading')}
                    actions={actions}
                    headerActions={headerActions}
                    content={content}
                    onClose={handleCloseSummarySidePanel}
                />
            );
        }, [dispatch, entity, handleCloseSummarySidePanel, itemId, loading, opened, ref, setSearchState, summaryType]);
    }
);
